import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { last, Observable } from 'rxjs';
import { API_ROUTES } from './api-routes';
import { environment } from '../../../environments/environment';
import { AppTokenResponse, OAuthAuthorize, SendMFACodeResponse, SendTempPasswordResponse, VerifyTempPasswordResponse } from '../interfaces/auth-interfaces';
import { User } from '../interfaces/user-interfaces';
import { DEFAULT_JSON_HEADERS, DEFAULT_URLENCODED_HEADERS } from './headers.config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(private http: HttpClient) { }

  /*
    Fetches an application token using client_id and client_secret.
    Uses default values from environment if parameters are not provided.
  */
  getAppToken(clientId: string = environment.clientId, clientSecret: string = environment.clientSecret): Observable<any> {
    const payload = {
      client_id: clientId,
      client_secret: clientSecret
    };

    return this.http.post<AppTokenResponse>(
      API_ROUTES.AUTH.APP_TOKEN,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  sendTempPassword(email: string, redirectUrl: string): Observable<any> {
    const payload = {
      email: email,
      redirect_url: redirectUrl
    };

    return this.http.post<SendTempPasswordResponse>(
      API_ROUTES.AUTH.SEND_TEMP_PASSWORD,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  resendTempPassword(email: string, redirectUrl: string): Observable<any> {
    const payload = {
      email: email,
      redirect_url: redirectUrl
    };

    return this.http.post<SendTempPasswordResponse>(
      API_ROUTES.AUTH.RESEND_TEMP_PASSWORD,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  verifyTempPassword(email: string, tempPassword: string): Observable<any> {
    const payload = {
      email: email,
      temp_password: tempPassword
    };

    return this.http.post<VerifyTempPasswordResponse>(
      API_ROUTES.AUTH.VERIFY_TEMP_PASSWORD,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  register(email: string, password: string, isActive: boolean = false, isSuperuser: boolean = false, isVerified: boolean = false, firstName: string, lastName: string, role: string = 'UNDEFINED'): Observable<any> {
    const payload = {
      email: email,
      password: password,
      is_active: isActive,
      is_superuser: isSuperuser,
      is_verified: isVerified,
      first_name: firstName,
      last_name: lastName,
      role: role
    };

    return this.http.post<User>(
      API_ROUTES.AUTH.REGISTER,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  login(email: string, password: string, grantType: string = 'password', scope: string = '', clientId: string = '', clientSecret: string = ''): Observable<any> {

    const params = new HttpParams().set('grant_type', grantType).set('username', email).set('password', password);

    return this.http.post(
      API_ROUTES.AUTH.LOGIN,
      params.toString(),
      {
        headers: DEFAULT_URLENCODED_HEADERS
      }
    );
  }

  logOut(): Observable<any> {
    const payload = {
    };

    return this.http.post<string>(
      API_ROUTES.AUTH.LOGOUT,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  forgotPassword(email: string): Observable<any> {
    const payload = {
      email: email
    };

    return this.http.post<null>(
      API_ROUTES.AUTH.FORGOT_PASSWORD,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    const payload = {
      token: token,
      password: newPassword
    };

    return this.http.post<null>(
      API_ROUTES.AUTH.RESET_PASSWORD,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  googleAuthorize(): Observable<any> {
    const payload = {
    };

    return this.http.get<OAuthAuthorize>(
      API_ROUTES.AUTH.GOOGLE_AUTHORIZE,
      payload
    );
  }

  linkedinAuthorize(): Observable<any> {
    const payload = {
    };

    return this.http.get<OAuthAuthorize>(
      API_ROUTES.AUTH.LINKEDIN_AUTHORIZE,
      payload
    );
  }

  sendMfa(): Observable<SendMFACodeResponse> {
    const payload = {
    };

    return this.http.post<SendMFACodeResponse>(
      API_ROUTES.AUTH.MFA_SEND,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  resendMfa(): Observable<SendMFACodeResponse> {
    const payload = {
    };

    return this.http.post<SendMFACodeResponse>(
      API_ROUTES.AUTH.MFA_RESEND,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  verifyMfa(code: string): Observable<any> {
    const payload = {
      code: code
    };

    return this.http.post<any>(
      API_ROUTES.AUTH.MFA_VERIFY,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }
}
